import { NICKNAME_REGEX } from '@principle-theorem/principle-core/interfaces';
import { removeAdditionalSpaces, splitName } from '@principle-theorem/shared';
import { first, last, trimEnd, trimStart } from 'lodash';

export class NameHelpers {
  private static readonly _titles = [
    'Dr',
    'Doctor',
    'Mr',
    'Mrs',
    'Ms',
    'Miss',
    'Prof',
    'Professor',
  ];

  /**
   * Gets string in brackets
   */
  static nickname(name: string): string {
    const nickname = first(new RegExp(NICKNAME_REGEX).exec(name));
    return trimEnd(trimStart(nickname, '('), ')');
  }

  static firstName(name: string): string {
    return first(splitName(NameHelpers.fullName(name))) ?? '';
  }

  /**
   * Strips title prefixes like Dr, Mr, Ms, etc.
   */
  static withoutTitle(name: string): string {
    const fullName = NameHelpers.fullName(name);
    const titleRegex = new RegExp(
      `^(${NameHelpers._titles.join('|')})\\b\\.?\\s*`,
      'i'
    );
    return fullName.replace(titleRegex, '').trim();
  }

  static lastName(name: string): string {
    const names = splitName(NameHelpers.fullName(name));
    return names.length > 1 ? last(names) ?? '' : '';
  }

  /**
   * Excluding Nickname
   */
  static fullName(name: string): string {
    const nickname = NameHelpers.nickname(name);
    const noNickname = name.replace(`(${nickname})`, '');
    return removeAdditionalSpaces(noNickname);
  }

  static initials(name: string): string {
    const matches = NameHelpers.fullName(name).match(/\b(\w)/g) || [];
    return matches.join('').toUpperCase();
  }

  /**
   * Avatar-style initials: drops title/honorific words (Dr, Mrs, Prof, …) and
   * takes the first letter of the first two remaining words. Falls back to the
   * name's first character when nothing survives the filtering, so an
   * honorific-only or single-word name still yields one initial.
   */
  static displayInitials(name: string): string {
    const words = NameHelpers.fullName(name)
      .split(/\s+/u)
      .filter((word) => word.length > 0 && !NameHelpers._isTitle(word));
    const initials = words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
    return initials || name.trim().charAt(0).toUpperCase();
  }

  private static _isTitle(word: string): boolean {
    const normalised = word.toLowerCase().replace(/\./gu, '');
    return NameHelpers._titles.some(
      (title) => title.toLowerCase() === normalised
    );
  }
}
